import React, { useState, useEffect } from 'react';
import { ChevronRight, Trash2, X } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import ContentDetailModal from '../components/modals/ContentDetailModal';
import ProgressBar from '../components/ui/ProgressBar';
import contentService from '../services/contentService';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const ChannelsPage = () => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelContent, setChannelContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [channelStats, setChannelStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [deletingContent, setDeletingContent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);

  const channels = [
    { name: 'Instagram', icon: '📷', color: 'from-pink-500 to-purple-600' },
    { name: 'YouTube', icon: '▶️', color: 'from-red-500 to-red-600' },
    { name: 'TikTok', icon: '🎵', color: 'from-gray-800 to-gray-900' },
    { name: 'Twitter', icon: '🐦', color: 'from-blue-400 to-blue-500' },
    { name: 'Facebook', icon: '👥', color: 'from-blue-600 to-blue-700' },
    { name: 'Pinterest', icon: '📌', color: 'from-red-500 to-pink-500' },
    { name: 'Threads', icon: '🧵', color: 'from-gray-600 to-gray-700' },
    { name: 'Podcasts', icon: '🎧', color: 'from-purple-500 to-indigo-600' },
    { name: 'Email', icon: '📧', color: 'from-gray-500 to-gray-600' },
    { name: 'Slack', icon: '💬', color: 'from-green-500 to-green-600' },
    { name: 'Web', icon: '🌐', color: 'from-blue-500 to-cyan-500' },
    { name: 'Recordings', icon: '🎙️', color: 'from-orange-500 to-red-500' }
  ];

  // Load channel stats on mount
  useEffect(() => {
    const loadChannelStats = async () => {
      if (!user?.id) return;
      
      setStatsLoading(true);
      try {
        const { data: stats } = await contentService.getContentStats(user.id);
        setChannelStats(stats || {});
      } catch (error) {
        console.error('Failed to load channel stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    loadChannelStats();
  }, [user?.id]);

  // Real-time updates for content
  useEffect(() => {
    if (!user?.id) return;

    const subscription = supabase
      .channel('channels-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'instagram_content',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        console.log('🔔 Real-time content update:', payload);
        console.log('📊 Current selectedChannel:', selectedChannel);
        console.log('📊 Payload channel:', payload.new?.channel);
        
        if (payload.eventType === 'INSERT') {
          // Update channel stats
          const channel = payload.new.channel || 'Web'; // Fallback for content without channel
          console.log('🎯 New content channel:', channel);
          if (channel) {
            setChannelStats(prev => {
              const newStats = {
                ...prev,
                [channel]: (prev[channel] || 0) + 1
              };
              console.log('📈 Updated channel stats:', newStats);
              return newStats;
            });
          }
          
          // Update channel content if viewing that channel
          if (selectedChannel === channel) {
            console.log('✅ Adding content to current channel view');
            setChannelContent(prev => {
              const newContent = [payload.new, ...prev];
              console.log('📝 Updated channel content:', newContent.length, 'items');
              return newContent;
            });
          } else {
            console.log('⚠️ Content not added to view - channel mismatch:', {
              selectedChannel,
              contentChannel: channel
            });
          }
        } else if (payload.eventType === 'UPDATE') {
          // Update content in current view if it matches
          if (selectedChannel === payload.new.channel) {
            setChannelContent(prev => 
              prev.map(item => 
                item.id === payload.new.id ? payload.new : item
              )
            );
          }
        } else if (payload.eventType === 'DELETE') {
          // Update channel stats
          const channel = payload.old.channel;
          if (channel) {
            setChannelStats(prev => ({
              ...prev,
              [channel]: Math.max((prev[channel] || 1) - 1, 0)
            }));
          }
          
          // Remove from current view
          setChannelContent(prev => 
            prev.filter(item => item.id !== payload.old.id)
          );
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id, selectedChannel]);

  // Load content for selected channel
  useEffect(() => {
    const loadChannelContent = async () => {
      if (!selectedChannel || !user?.id) {
        console.log('⚠️ Missing required data:', { selectedChannel, userId: user?.id });
        return;
      }
      
      setLoading(true);
      try {
        console.log('🔄 Loading content for channel:', selectedChannel);
        const { data: content, error } = await contentService.getContentByChannel(user.id, selectedChannel);
        
        if (error) {
          console.error('❌ Error loading channel content:', error);
          console.log('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details
          });
          setChannelContent([]);
        } else {
          console.log('✅ Loaded content:', content?.length || 0, 'items for', selectedChannel);
          setChannelContent(content || []);
        }
      } catch (error) {
        console.error('❌ Failed to load channel content:', error);
        console.log('Exception details:', {
          message: error.message,
          stack: error.stack
        });
        setChannelContent([]);
      } finally {
        setLoading(false);
      }
    };

    loadChannelContent();
  }, [selectedChannel, user?.id]);

  const handleChannelSelect = (channelName) => {
    console.log('🎯 Selecting channel:', channelName);
    try {
      setSelectedChannel(channelName);
      setChannelContent([]); // Clear previous content
      console.log('✅ Channel selection successful');
    } catch (error) {
      console.error('❌ Error selecting channel:', error);
    }
  };

  const getChannelCount = (channelName) => {
    if (statsLoading) {
      return '...';
    }
    return channelStats[channelName] || 0;
  };

  const handleDeleteContent = async (contentId) => {
    if (!user) return;

    setDeletingContent(contentId);
    try {
      const { error } = await contentService.deleteContent(user.id, contentId);
      if (error) throw error;

      // Remove from local state
      setChannelContent(prev => prev.filter(item => item.id !== contentId));
    } catch (error) {
      console.error('Failed to delete content:', error);
    } finally {
      setDeletingContent(null);
      setShowDeleteModal(false);
      setContentToDelete(null);
    }
  };

  const confirmDelete = (content) => {
    setContentToDelete(content);
    setShowDeleteModal(true);
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
      
      {/* Sidebar */}
      <Sidebar 
        isExpanded={isExpanded} 
        onToggle={() => setIsExpanded(!isExpanded)} 
      />
      
      {/* Arrow for minimized sidebar */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="fixed left-16 top-8 w-6 h-6 bg-white/40 backdrop-blur-lg rounded-r-lg flex items-center justify-center text-gray-800 hover:bg-white/50 transition-all duration-500 ease-out z-[60] shadow-lg border border-white/30"
        >
          <ChevronRight size={16} />
        </button>
      )}
      
      {/* Main Content */}
      <main className={`flex-1 flex flex-col transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Channels</h1>
              <p className="text-gray-600 text-lg">Browse your content by platform and source</p>
            </div>

            {/* Channel Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
              {channels.map((channel, index) => (
                <button
                  key={index}
                  onClick={() => handleChannelSelect(channel.name)}
                  className={`bg-white/40 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl p-8 hover:bg-white/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 text-left ${
                    selectedChannel === channel.name ? 'ring-2 ring-blue-500 bg-white/60' : ''
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${channel.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <span className="text-white text-2xl">{channel.icon}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg">{channel.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{getChannelCount(channel.name)} items</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Channel Content */}
            {selectedChannel && (
              <div className="bg-white/40 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${
                      channels.find(c => c.name === selectedChannel)?.color || 'from-gray-500 to-gray-600'
                    } rounded-xl flex items-center justify-center shadow-lg`}>
                      <span className="text-white text-xl">
                        {channels.find(c => c.name === selectedChannel)?.icon || '📄'}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900">{selectedChannel}</h2>
                      <p className="text-gray-600">{channelContent.length} items</p>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading content...</span>
                  </div>
                ) : channelContent.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {channelContent.map((item, index) => (
                      <div 
                        key={index} 
                        className="bg-white/60 backdrop-blur-lg rounded-2xl border border-white/50 shadow-lg p-4 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer relative"
                      >
                        {/* Delete Button - Top Right Corner */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(item);
                          }}
                          disabled={deletingContent === item.id}
                          className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors z-10"
                          title="Delete content"
                        >
                          <X size={16} />
                        </button>

                        {/* Clickable Content */}
                        <div 
                          onClick={() => {
                            setSelectedContent(item);
                            setShowContentModal(true);
                          }}
                          className="cursor-pointer"
                        >
                          {/* Thumbnail */}
                          <div className="mb-4">
                            {item.thumbnail_url ? (
                              <img 
                                src={item.thumbnail_url} 
                                alt={item.title}
                                className="w-full h-32 object-cover rounded-xl"
                              />
                            ) : (
                              <div className="w-full h-32 bg-gray-200 rounded-xl flex items-center justify-center">
                                <span className="text-4xl">{item.platform_icon || '📄'}</span>
                              </div>
                            )}
                          </div>

                          {/* Title */}
                          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{item.title}</h3>

                          {/* Processing Status with Progress Bar */}
                          {item.analysis_status === 'processing' && (
                            <div className="mb-2">
                              <ProgressBar 
                                percentage={item.progress_percentage || 0}
                                status={item.progress_status || 'processing'}
                                showIcon={true}
                                showLabel={true}
                                size="sm"
                              />
                            </div>
                          )}

                          {/* AI Summary */}
                          {item.ai_analysis?.summary && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.ai_analysis.summary}</p>
                          )}

                          {/* Key Points Preview */}
                          {item.key_points && item.key_points.length > 0 && (
                            <div className="mb-3">
                              <div className="text-xs text-gray-500 mb-1 font-medium">Key Points:</div>
                              <ul className="text-xs text-gray-600 space-y-1">
                                {item.key_points.slice(0, 2).map((point, pointIndex) => (
                                  <li key={pointIndex} className="flex items-start">
                                    <span className="text-blue-500 mr-1">•</span>
                                    <span className="line-clamp-1">{point}</span>
                                  </li>
                                ))}
                                {item.key_points.length > 2 && (
                                  <li className="text-gray-400 text-xs">
                                    +{item.key_points.length - 2} more points
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Tags */}
                          {item.tags && item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {item.tags.slice(0, 3).map((tag, tagIndex) => (
                                <span key={tagIndex} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  {tag}
                                </span>
                              ))}
                              {item.tags.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  +{item.tags.length - 3}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Status and Date */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              {item.analysis_status === 'completed' && (
                                <span className="text-xs text-green-600">✓ Analyzed</span>
                              )}
                              {item.transcription?.full_transcript && (
                                <span className="text-xs text-blue-600">📝 Transcript</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {new Date(item.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">
                      {channels.find(c => c.name === selectedChannel)?.icon || '📄'}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No content yet</h3>
                    <p className="text-gray-600">Upload content to see it appear in {selectedChannel}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Content Detail Modal */}
      <ContentDetailModal
        isOpen={showContentModal}
        onClose={() => {
          setShowContentModal(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && contentToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <X size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Content</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete <strong>"{contentToDelete.title}"</strong>?
              </p>
              <p className="text-sm text-gray-500">
                This will permanently remove the content and all its analysis data.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setContentToDelete(null);
                }}
                className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteContent(contentToDelete.id)}
                disabled={deletingContent === contentToDelete.id}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {deletingContent === contentToDelete.id ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <X size={16} />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelsPage;
